import { Text, View, ScrollView, Linking, TouchableOpacity, Platform, Pressable, useWindowDimensions, InteractionManager } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import MaterialIcon from "@react-native-vector-icons/material-icons";
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import Avatar from '@/components/People/Avatar';
import PersonTasksTab from '@/components/People/PersonTasksTab';
import { archivePerson, changeAdminStatus, getPersonProfile } from '@/data/people';
import { isRoleVisibleToUser } from '@/data/roles';
import { sendInvitation } from '@/data/registrations';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ProfileCard from '@/components/People/ProfileCard';
import { Tooltip } from '@/components/General/Tooltip';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { getOrganization, getCurrentOrgID } from '@/data/organization';
import useLoader from '@/utils/hooks/useLoader';
import { parseTimeFrame } from '@/utils/dater';
import SmallGroupCard from '@/components/SmallGroups/SmallGroupCard';
import { LegendList } from "@legendapp/list";
import Toast from 'react-native-toast-message';
import BlackoutPeriodItem from '@/components/Events/BlackoutPeriodItem';
import Calendar from '@/components/Events/Calendar';
import { RefreshControl } from 'react-native';
import useAuthStore from '@/data/store/useAuthStore';
import usePlatform from '@/utils/hooks/usePlatform';
import { DateTime } from 'luxon';
import ContentLoader from '@/components/General/ContentLoader';
import { getFile, getSignedStorageFileURL } from '@/utils/FileManager';
import { showAlert } from '@/utils/showAlert';
import FileViewer from "react-native-file-viewer";
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SolidButton from '@/components/General/SolidButton';
import SegmentedTabs from '@/components/General/SegmentedTabs';
import SelectInput from '@/components/General/inputs/SelectInput';
import { useLocalSearchParams, useRouter, useNavigation } from 'expo-router';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import Unicon from '@/components/General/Unicon';
import ActionButton from '@/components/General/ActionButton';
import { ActivityIndicator } from 'react-native-paper';
import * as Sentry from '@sentry/react-native';
import Chart from '@/components/Charts/Chart';
import TrainingProgressTab from '@/components/Training/TrainingProgressTab';
import ContextMenu, { contextMenuIcons } from '@/components/General/ContextMenu';
import Clipboard from '@react-native-clipboard/clipboard';
import PersonProfilePDF from '@/components/People/PersonProfilePDF';
import GivingTab from '@/components/Contributions/GivingTab';
import FilesScreen from '../files/index';
import NotesTab from '@/components/People/NotesTab';
import FollowUpTab from '@/components/People/FollowUpTab';
import { saveToRecents } from '@/utils/recentlyVisited';
import { openURL } from '@/utils/linking';
import SearchBox from '@/components/General/inputs/SearchBox';
import PersonMilestonesTab from '@/components/Milestones/PersonMilestonesTab';
import { MILESTONES_ENABLED } from '@/config/milestones';
import { canViewPersonMilestones } from '@/utils/milestonePermissions';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg, canViewModule } from '@/utils/organizationEntitlements';

const formatBlackoutTime = (isoString) => {
    const d = new Date(isoString);
    return d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const GROUP_STATUS_OPTIONS = [
    { label: "Active", value: "ACTIVE" },
    { label: "Paused", value: "PAUSED" },
    { label: "Completed", value: "COMPLETED" },
    { label: "Canceled", value: "CANCELED" },
    { label: "All Statuses", value: "ALL" },
];

const SmallGroupsTab = ({ profile }) => {
    const [viewTypeOverride, setViewTypeOverride] = useState(null);
    const [typeFilter, setTypeFilter] = useState("ALL");
    const [statusFilter, setStatusFilter] = useState("ACTIVE");

    const { data: smallGroupTypes } = Query.smallGroupTypes.all({});

    const viewType = viewTypeOverride ?? (profile?.SmallGroupsAsMember?.length > 0 ? "member" : "leader");

    const typeOptions = useMemo(() => ([
        { label: "All Types", value: "ALL" },
        ...(smallGroupTypes?.map(t => ({ label: t.name, value: t.id })) || []),
    ]), [smallGroupTypes]);

    const groups = useMemo(() => {
        const source = viewType === "member" ? profile?.SmallGroupsAsMember : profile?.SmallGroupsAsLeader;
        return (source || []).filter(g => {
            if (typeFilter !== "ALL" && g?.SmallGroupType?.id !== typeFilter) return false;
            if (statusFilter !== "ALL" && g?.status !== statusFilter) return false;
            return true;
        });
    }, [viewType, typeFilter, statusFilter, profile?.SmallGroupsAsMember, profile?.SmallGroupsAsLeader]);

    return (
        <View style={styles.tabContent}>
            <View style={styles.groupsFilters}>
                <SegmentedTabs
                    tabs={[{ key: 'member', label: 'As Member' }, { key: 'leader', label: 'As Leader' }]}
                    value={viewType}
                    onChange={setViewTypeOverride}
                    testIDPrefix="person-groups-view"
                />
                <View style={styles.groupsSelectRow}>
                    <View style={styles.groupsSelect}>
                        <SelectInput title="Type" placeholder="All Types" value={typeFilter} options={typeOptions} hideClearButton onUpdate={setTypeFilter} />
                    </View>
                    <View style={styles.groupsSelect}>
                        <SelectInput title="Status" placeholder="All Statuses" value={statusFilter} options={GROUP_STATUS_OPTIONS} hideClearButton onUpdate={setStatusFilter} />
                    </View>
                </View>
            </View>
            <LegendList
                recycleItems
                keyExtractor={(item) => item?.id}
                estimatedItemSize={100}
                data={groups}
                renderItem={({ item }) => <SmallGroupCard smallGroup={item} />}
                ListEmptyComponent={<EmptyListPlaceholder message="No Small Groups" />}
                keyboardShouldPersistTaps="handled"
            />
        </View>
    );
};

const BlackoutCalendarTab = ({ profile, loggedInUser, router }) => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [monthShown, setMonthShown] = useState(new Date().getMonth());
    const [yearShown, setYearShown] = useState(new Date().getFullYear());
    const { isPortrait } = usePlatform();
    const { theme: { colors: themeColors } } = useUnistyles();

    const { data: blackoutPeriods = [] } = Query.blackoutDates.byMonth({
        month: monthShown,
        year: yearShown,
        personID: profile?.id,
        options: { enabled: !!profile?.id },
    });

    const blackoutDatesMap = useMemo(() => {
        const map = {};
        blackoutPeriods.forEach(blackout => {
            const startMidnight = new Date(blackout.startDate);
            startMidnight.setHours(0, 0, 0, 0);
            const endMidnight = new Date(blackout.endDate);
            endMidnight.setHours(0, 0, 0, 0);
            const cursor = new Date(startMidnight);
            while (cursor <= endMidnight) {
                if (!map[cursor.toDateString()]) map[cursor.toDateString()] = 'partial';
                cursor.setDate(cursor.getDate() + 1);
            }
        });
        return map;
    }, [blackoutPeriods]);

    const blackoutEventsByDate = useMemo(() => {
        if (isPortrait) return {};
        const byDate = {};
        blackoutPeriods.forEach(blackout => {
            const startDate = new Date(blackout.startDate);
            const endDate = new Date(blackout.endDate);
            const startMidnight = new Date(startDate); startMidnight.setHours(0, 0, 0, 0);
            const endMidnight = new Date(endDate); endMidnight.setHours(0, 0, 0, 0);

            const cursor = new Date(startMidnight);
            while (cursor <= endMidnight) {
                const dateString = cursor.toDateString();
                if (!byDate[dateString]) byDate[dateString] = [];
                const isFirstDay = cursor.toDateString() === startMidnight.toDateString();
                const isLastDay = cursor.toDateString() === endMidnight.toDateString();
                const startHour = startDate.getHours();
                const startMin = startDate.getMinutes();
                const endHour = endDate.getHours();
                const endMin = endDate.getMinutes();
                const isAllDay = startHour === 0 && startMin === 0 && (endHour > 22 || (endHour === 23 && endMin >= 59));

                let label = 'All Day';
                if (!isAllDay) {
                    if (isFirstDay && isLastDay) {
                        label = `${formatBlackoutTime(blackout.startDate)} – ${formatBlackoutTime(blackout.endDate)}`;
                    } else if (isFirstDay) {
                        label = `From ${formatBlackoutTime(blackout.startDate)}`;
                    } else if (isLastDay) {
                        label = `Until ${formatBlackoutTime(blackout.endDate)}`;
                    } else {
                        label = 'All Day';
                    }
                }

                byDate[dateString].push({
                    id: 'blackout_' + blackout.id + '_' + dateString,
                    name: label,
                    startTime: null,
                    allDay: true,
                    color: themeColors.events,
                });
                cursor.setDate(cursor.getDate() + 1);
            }
        });
        return byDate;
    }, [blackoutPeriods, isPortrait, themeColors.events]);

    return (
        <ScrollView contentContainerStyle={blackoutTabStyles.scrollContent} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
            <View style={blackoutTabStyles.calendarContainer}>
                {profile?.id === loggedInUser?.id && (
                    <View style={blackoutTabStyles.addBtnRow}>
                        <SolidButton size="sm" iconName="add" text="Add Blackout Date" onPress={() => router.push("/events/blackouts/create")} />
                    </View>
                )}
                <Calendar
                    blackoutDates={blackoutDatesMap}
                    eventsByDate={blackoutEventsByDate}
                    onDayPress={(date) => setSelectedDate(date)}
                    onMonthChange={(m) => setMonthShown(m)}
                    onYearChange={(y) => setYearShown(y)}
                    canCreateEvents={false}
                    dayBoxHeightOverride={isPortrait ? undefined : 70}
                    disableSelection={true}
                />
                <View style={blackoutTabStyles.daySection}>
                    <Text style={blackoutTabStyles.daySectionTitle}>
                        {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </Text>
                    {blackoutDatesMap[selectedDate.toDateString()] ? (
                        blackoutPeriods
                            .filter(b => {
                                const s = new Date(b.startDate); s.setHours(0,0,0,0);
                                const e = new Date(b.endDate); e.setHours(0,0,0,0);
                                const sel = new Date(selectedDate); sel.setHours(0,0,0,0);
                                return sel >= s && sel <= e;
                            })
                            .map(item => (
                                <BlackoutPeriodItem
                                    key={item.id}
                                    blackoutPeriod={{...item, Person: {firstName: profile?.firstName, lastName: profile?.lastName}}}
                                />
                            ))
                    ) : (
                        <EmptyListPlaceholder message="No blackout periods on this day" />
                    )}
                </View>
            </View>
        </ScrollView>
    );
};

const blackoutTabStyles = StyleSheet.create((theme) => ({
    scrollContent: {
        padding: 12,
        paddingBottom: 100,
        alignItems: 'center',
    },
    calendarContainer: {
        width: '100%',
        maxWidth: 600,
    },
    addBtnRow: {
        marginBottom: 12,
    },
    daySection: {
        marginTop: 16,
    },
    daySectionTitle: {
        fontSize: 16,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
        marginBottom: 10,
    },
}));

const DATE_RANGE_OPTIONS = [
    { key: '30d', label: '30 Days', days: 30 },
    { key: '90d', label: '90 Days', days: 90 },
    { key: '6mo', label: '6 Months', days: 180 },
    { key: '12mo', label: '12 Months', days: 365 },
    { key: 'all', label: 'All Time', days: null },
];

const AttendanceTab = ({ personID }) => {
    const [selectedRange, setSelectedRange] = useState('90d');
    const [startDate, setStartDate] = useState(DateTime.now().minus({ days: 90 }));
    const [endDate, setEndDate] = useState(DateTime.now());
    const [eventName, setEventName] = useState(null);

    const handleRangeChange = useCallback((rangeKey) => {
        setSelectedRange(rangeKey);
        const option = DATE_RANGE_OPTIONS.find(o => o.key === rangeKey);
        if (option) {
            setEndDate(DateTime.now());
            if (option.days === null) {
                setStartDate(DateTime.fromISO('2000-01-01'));
            } else {
                setStartDate(DateTime.now().minus({ days: option.days }));
            }
        }
    }, []);

    const { data: attendanceData, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = Query.people.attendanceHistory({
        personID,
        eventName,
        startDate,
        endDate,
        options: { enabled: !!personID }
    });

    const {isWeb} = usePlatform();
    const router = useRouter();

    const attendance = attendanceData?.pages?.flatMap(page => page.data) || [];

    const renderAttendanceItem = useCallback(({ item }) => {
        const eventInstance = item.EventInstance;
        const event = eventInstance?.Event;
        const calendarColor = event?.Calendar?.color || '#6366f1';
        
        return (
            <Pressable 
                onPress={() => router.push(`/events/instances/${eventInstance?.id}`)}
                style={[attendanceStyles.attendanceItem]}
            >
                <View style={[attendanceStyles.colorBar, { backgroundColor: calendarColor }]} />
                <View style={attendanceStyles.attendanceContent}>
                    <Text style={attendanceStyles.eventName}>{event?.name || 'Unknown Event'}</Text>
                    <Text style={attendanceStyles.eventDate}>
                        {DateTime.fromISO(eventInstance?.startTime).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
                    </Text>
                    {item.Location?.id && (
                        <TouchableOpacity onPress={() => router.push('/locations/' + item.Location.id)}>
                            <Text style={[attendanceStyles.locationText, attendanceStyles.locationTextLink]}>{item.Location.name}</Text>
                        </TouchableOpacity>
                    )}
                </View>
                <View style={attendanceStyles.timeContainer}>
                    <Text style={attendanceStyles.checkInTime}>
                        {DateTime.fromISO(item.createdAt).toLocaleString(DateTime.TIME_SIMPLE)}
                    </Text>
                    {item.checkedOutAt && (
                        <Text style={attendanceStyles.checkOutTime}>
                            Out: {DateTime.fromISO(item.checkedOutAt).toLocaleString(DateTime.TIME_SIMPLE)}
                        </Text>
                    )}
                </View>
            </Pressable>
        );
    }, [router]);

    return (
        <View style={attendanceStyles.container}>
            {/* Date Range Picker */}
            <View style={attendanceStyles.dateRangeContainer}>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                    <View style={attendanceStyles.dateRangeContainer}>
                        {DATE_RANGE_OPTIONS.map((option) => (
                            <Pressable
                                key={option.key}
                                onPress={() => handleRangeChange(option.key)}
                                style={[
                                    attendanceStyles.dateRangeButton,
                                    selectedRange === option.key && attendanceStyles.dateRangeButtonActive
                                ]}
                            >
                                <Text style={[
                                    attendanceStyles.dateRangeText,
                                    selectedRange === option.key && attendanceStyles.dateRangeTextActive
                                ]}>
                                    {option.label}
                                </Text>
                            </Pressable>
                        ))}
                    </View>
                </ScrollView>
            </View>

            <View style={attendanceStyles.attendanceContentContainer}>
                <View style={attendanceStyles.chartGrid}>
                    <Chart
                        height={isWeb ? 300 : 250} 
                        chartName="AttendancePerEventChart" 
                        parameters={{
                            personID,
                            startDate: startDate.toUTC().toFormat("yyyy-MM-dd"), 
                            endDate: endDate.toUTC().toFormat("yyyy-MM-dd")
                        }}
                        onClick={(label, _value) => setEventName(label)}
                    />
                </View>

                {/* Event Filter Display */}
                <View style={attendanceStyles.filterContainer}>
                    <Text style={attendanceStyles.filterLabel}>
                        Showing: <Text style={attendanceStyles.filterValue}>{eventName || 'All Events'}</Text>
                    </Text>
                    {eventName && (
                        <Pressable onPress={() => setEventName(null)} style={attendanceStyles.resetButton}>
                            <Text style={attendanceStyles.resetButtonText}>Show All Events</Text>
                        </Pressable>
                    )}
                </View>

                <View style={attendanceStyles.listContainer}>
                    <LegendList
                        recycleItems
                        keyExtractor={(item) => `${item.EventInstance?.id}-${item.createdAt}`}
                        estimatedItemSize={80}
                        data={isLoading ? [] : attendance}
                        renderItem={renderAttendanceItem}
                        contentContainerStyle={{ padding: 10, paddingBottom: 100 }}
                        onEndReached={() => hasNextPage && fetchNextPage()}
                        onEndReachedThreshold={0.5}
                        ListFooterComponent={isFetchingNextPage || isLoading ? <ActivityIndicator size="small" /> : null}
                        ListEmptyComponent={(!isLoading && !isFetchingNextPage) && <EmptyListPlaceholder message="No attendance records" />}
                        keyboardShouldPersistTaps="handled"
                    />
                </View>
            </View>
        </View>
    );
};

const attendanceStyles = StyleSheet.create((theme, rt) => ({
    container: { flex:1 },
    loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
    loadingText: { color: theme.colors.textSecondary, fontSize: 14 },
    chartGrid: { padding: 10, height: rt.isPortrait ? 270 : 320 },
    attendanceContentContainer: { 
        flex: 1,
        _web: {
            overflow: 'hidden',
        }
    },
    listContainer: {
        flex: 1,
        _web: {
            overflow: 'auto',
        }
    },
    
    // Date Range Picker
    dateRangeContainer: { 
        flexDirection: 'row', 
        gap: 8, 
        paddingHorizontal: 10, 
        paddingTop: 5,
    },
    dateRangeButton: { 
        paddingHorizontal: 12, 
        paddingVertical: 6, 
        borderRadius: 16, 
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border
    },
    dateRangeButtonActive: { 
        backgroundColor: theme.colors.primary,
        borderColor: theme.colors.primary
    },
    dateRangeText: { 
        fontSize: 12, 
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.medium
    },
    dateRangeTextActive: { 
        color: theme.colors.surface,
        fontFamily: theme.fonts.bold
    },

    // Filter Container
    filterContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
        marginHorizontal: 10,
        marginBottom: 4,
        borderRadius: 8,
    },
    filterLabel: {
        fontSize: 13,
        color: theme.colors.textSecondary
    },
    filterValue: {
        fontFamily: theme.fonts.bold,
        color: theme.colors.text
    },
    resetButton: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 4,
        backgroundColor: theme.colors.primaryContainer
    },
    resetButtonText: {
        fontSize: 12,
        color: theme.colors.primary,
        fontFamily: theme.fonts.medium
    },

    // Attendance Item
    attendanceItem: { 
        backgroundColor: theme.colors.surface, 
        borderRadius: 10, 
        marginBottom: 8, 
        flexDirection: 'row', 
        alignItems: 'center',
        overflow: 'hidden',
        boxShadow: theme.shadows.card,
        _web: {
            transition: "all 0.2s ease-in-out",
            _hover: {
                backgroundColor: theme.colors.hover,
            }
        }
    },
    colorBar: { width: 4, alignSelf: 'stretch' },
    attendanceContent: { flex: 1, padding: 14 },
    eventName: { fontSize: 14, fontFamily: theme.fonts.bold, color: theme.colors.text },
    eventDate: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 },
    locationText: { fontSize: 11, color: theme.colors.textSecondary, marginTop: 4, fontStyle: 'italic' },
    locationTextLink: { color: theme.colors.primary, textDecorationLine: 'underline' },
    timeContainer: { paddingRight: 14, alignItems: 'flex-end' },
    checkInTime: { fontSize: 12, color: theme.colors.textSecondary },
    checkOutTime: { fontSize: 11, color: theme.colors.textSecondary, marginTop: 2 },
}));

const PersonProfileNew = (props) => {
    const loader = useLoader();
    const setEmulation = useAuthStore((state) => state.setEmulation);
    const params = useLocalSearchParams();
    const personID = props.personID || params.personID;
    const fromScreen = props.fromScreen ?? `/people/${personID}`;
    const tab = props.tab || params.tab;
    const forceSingleColumn = props.forceSingleColumn === true;
    
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { width } = useWindowDimensions();

    const [notFound, setNotFound] = useState(null);
    const [activityTab, setActivityTab] = useState(0);
    const [rolesViewType, setRolesViewType] = useState("roles");
    const [mobileTab, setMobileTab] = useState(0);

    const [initialPageApplied, setInitialPageApplied] = useState(false);
    const [showPDFExport, setShowPDFExport] = useState(false);

    const { entitlements } = useOrganizationEntitlements();
    // Custom fields are paid-only — Free profiles never carry values and the
    // sections/config tables are denied, so don't mount the query at all.
    const isFree = isFreeOrg(entitlements);
    const { data: profile, isFetching: fetchingProfile } = Query.people.byId({ id: personID, options: {enabled: !!personID, placeholderData: {}}});
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: personCustomFieldSections } = Query.personCustomFieldSections.all({ options: { enabled: !isFree } });
    const { data: trainingProgress } = Query.trainingProgress.byPersonId({ personID, options: { enabled: !!personID && !isFree } });

    // Track recently visited — skip when rendered inside a modal (e.g. duplicate review)
    useEffect(() => {
        if (profile?.id && !forceSingleColumn) {
            saveToRecents({
                id: profile.id,
                url: `/people/${profile.id}`,
                title: `${profile.firstName} ${profile.lastName}`,
                params: { personID: profile.id },
            });
        }
    }, [profile?.id, forceSingleColumn]);

    const { isNative, isWeb } = usePlatform();
    const isWideScreen = !forceSingleColumn && width >= 900;

    const loggedInUserIsAdmin = loggedInUser?.RegisteredUsersOrganizations?.[0]?.isAdmin;

    // Non-admins cannot see the Permissions sub-tab — force to Roles
    useEffect(() => {
        if (loggedInUser && !loggedInUserIsAdmin) {
            setRolesViewType("roles");
        }
    }, [loggedInUser, loggedInUserIsAdmin]);
    const currentProfileIsAdmin = profile?.RegisteredUsersOrganizations?.[0]?.isAdmin;
    const currentProfileIsRegistered = profile?.RegisteredUsersOrganizations?.length > 0;

    // A user can always see their own profile fields (contact info, basic info, custom fields),
    // regardless of View permissions. Edit capability is still governed by Edit People.
    const isOwnProfile = loggedInUser?.id === profile?.id;
    const canView = (permission) => isOwnProfile || loggedInUser?.can?.(permission);
    const canViewContributions = loggedInUser?.can?.('View Contributions');

    const adminStatusMutation = useMutation({
        mutationFn: changeAdminStatus,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(personID) })
    });

    const archivePersonMutation = useMutation({
        mutationFn: archivePerson,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(personID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.all() });
            const isArchived = profile?.isArchived;
            if (isArchived) {
                Toast.show({ type: 'success', text2: 'Person unarchived successfully' });
            } else {
                router.back();
                Toast.show({ type: 'success', text2: 'Person archived successfully' });
            }
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to update person' });
        }
    });


    useEffect(() => {
        if(profile && loggedInUser){
            Sentry.metrics.count("view_profile", 1, {
                attributes: {
                    profile_id: profile?.id,
                    profile_name: profile?.firstName + " " + profile?.lastName,
                },
            });
        }
    }, [profile, loggedInUser]);

    const effectivePermissions = useMemo(() => {
        const permMap = {};
        profile?.Roles?.filter(r => isRoleVisibleToUser(r, loggedInUser))?.forEach(role => {
            role?.Permissions?.forEach(perm => {
                if (!permMap[perm?.name]) {
                    permMap[perm?.name] = { ...perm, grantedByRoles: [] };
                }
                if (!permMap[perm?.name].grantedByRoles.map(r => r.roleID).includes(role.id)) {
                    permMap[perm?.name].grantedByRoles.push({roleName: role.name, roleID: role.id});
                }
            });
        });
        return Object.values(permMap).sort((a, b) => a?.name?.localeCompare(b?.name));
    }, [profile?.Roles, loggedInUser]);

    const effectivePermissionsByModule = useMemo(() => {
        const moduleMap = {};
        effectivePermissions.forEach(perm => {
            if (!moduleMap[perm.module]) moduleMap[perm.module] = [];
            moduleMap[perm.module].push(perm);
        });
        return Object.entries(moduleMap).sort(([a], [b]) => a.localeCompare(b));
    }, [effectivePermissions]);

    const [permissionsSearchQuery, setPermissionsSearchQuery] = useState('');

    const filteredPermissionsByModule = useMemo(() => {
        if (!permissionsSearchQuery) return effectivePermissionsByModule;
        const lowerQuery = permissionsSearchQuery.toLowerCase();
        return effectivePermissionsByModule.map(([moduleName, modulePermissions]) => {
            const filtered = modulePermissions.filter(p =>
                p.name?.toLowerCase().includes(lowerQuery) ||
                p.description?.toLowerCase().includes(lowerQuery) ||
                moduleName.toLowerCase().includes(lowerQuery)
            );
            return [moduleName, filtered];
        }).filter(([_, perms]) => perms.length > 0);
    }, [effectivePermissionsByModule, permissionsSearchQuery]);

    const family = useMemo(() => (profile?.Family?.FamilyMembers || []).filter(fm => fm.id !== profile?.id), [profile?.Family?.FamilyMembers, profile?.id]);
    // Free orgs never receive customFieldValues — keep the memo empty so no
    // stale paid cache data can render downstream (profile panel, PDF export).
    const customFieldValues = useMemo(() => isFree ? {} : (profile?.customFieldValues || {}), [isFree, profile?.customFieldValues]);


    const activityRoutes = useMemo(() => {
        const tabs = [{ key: 'tasks', title: 'Tasks', testID: 'person-tab-tasks' }];
        tabs.push({ key: 'attendance', title: 'Attendance' });
        if (profile?.SmallGroupsAsLeader?.length > 0 || profile?.SmallGroupsAsMember?.length > 0) {
            tabs.push({ key: 'groups', title: 'Small Groups' });
        }
        
        tabs.push({ key: 'roles', title: 'Roles' });
        
        if (family.length > 0) {
            tabs.push({ key: 'family', title: 'Family' });
        }
        if (loggedInUser?.can?.('View People Notes') || loggedInUserIsAdmin) {
            tabs.push({ key: 'notes', title: 'Notes' });
        }
        // Follow Up rides the 'paths' module entitlement (P0 G02) and is
        // team-gated by its own permission — never 'View Paths'. Free orgs
        // drop it via the isFree filter below (not in the allowlist).
        if (loggedInUser?.can?.('View Follow Up') && canViewModule(entitlements, 'paths')) {
            tabs.push({ key: 'followup', title: 'Follow Up', testID: 'person-tab-followup' });
        }
        tabs.push({ key: 'files', title: 'Files' });
        tabs.push({ key: 'training', title: 'Training' });
        tabs.push({ key: 'blackouts', title: 'Blackouts' });
        // Milestone badges: own profile or View Milestones. The scene mounts
        // lazily via the TabView `lazy` prop, so its RPC never fires until
        // the tab is first selected.
        if (MILESTONES_ENABLED && canViewPersonMilestones(loggedInUser, personID)) {
            tabs.push({ key: 'badges', title: 'Badges', testID: 'person-tab-badges' });
        }
        if (loggedInUser?.id === personID || loggedInUser?.can?.('View Contributions')) {
            tabs.push({ key: 'giving', title: 'Giving' });
        }
        return isFree ? tabs.filter(({ key }) => ['family', 'notes', 'giving'].includes(key)) : tabs;
    }, [profile, loggedInUser, loggedInUserIsAdmin, family.length, isFree, personID, entitlements]);

    const getAgeString = (dateOfBirth) => {
        const today = DateTime.now();
        const birthDate = DateTime.fromISO(dateOfBirth);
        let age = today.year - birthDate.year;
        const m = today.month - birthDate.month;
        if (m < 0 || (m === 0 && today.day < birthDate.day)) age--;
        if (age === 0) {
            let months = m < 0 ? m + 12 : m;
            if (today.day < birthDate.day) months--;
            return months === 1 ? '1 month' : `${months} months`;
        }
        return age === 1 ? '1 year' : `${age} years`;
    };

    const formatCustomFieldValue = (value, type) => {
        if (!value) return "";
        switch (type) {
            case "Text":
            case "Long Text":
            case "Number":
            case "Dropdown":
                return value;
            case "Multiselect":
                return Array.isArray(value) ? value.join(", ") : value;
            case "Date": {
                let localFormat = DateTime.fromFormat(value, "MM/dd/yyyy");
                if (!localFormat.isValid) localFormat = DateTime.fromSQL(value);
                if (!localFormat.isValid) localFormat = DateTime.fromISO(value);
                return localFormat.toLocaleString(DateTime.DATE_MED);
            }
            case "Checkbox":
                return value ? "Yes" : "No";
            case "File Upload": {
                // If it's a URL string, return it
                if (typeof value === 'string') return value;
                // Otherwise handle object/array
                const fileObj = Array.isArray(value) ? value[0] : value;
                return fileObj?.name || fileObj?.filename;
            }
            default:
                return value;
        }
    };

    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(personID) });

    const handlePhonePress = (number) => {
        showAlert({
            title: `Call or text ${profile?.firstName}?`,
            subtitle: '',
            extraButton: { text: 'Call', onPress: () => openURL(`tel:${number.replace(" ", "")}`) },
            confirmText: 'Text',
            onConfirm: () => openURL(`sms:${number.replace(" ", "")}`),
        });
    };

    const handleFileOpen = async (value) => {
        loader.enable();
        // If it's a URL string, open it in browser
        if (typeof value === 'string') {
            // Validate the URL has a host/path beyond the scheme — a bare
            // "https://" (e.g. an empty Website custom field) rejects on iOS
            // with "Unable to open URL: https://". See CHURCH-APP-12R.
            try {
                const parsed = new URL(value);
                if (!parsed.host) throw new Error('no host');
            } catch {
                loader.disable();
                Toast.show({ type: 'error', text2: 'That link is incomplete and can\'t be opened.' });
                return;
            }
            Linking.openURL(value)
                .then(() => loader.disable())
                .catch(() => {
                    loader.disable();
                    Toast.show({ type: 'error', text2: 'Unable to open that link.' });
                });
            return;
        }
        // Otherwise handle object/array
        const fileObj = Array.isArray(value) ? value[0] : value;
        if (Platform.OS === 'web') {
            const signedUrl = await getSignedStorageFileURL(fileObj.key);
            if (!signedUrl) {
                loader.disable();
                Toast.show({ type: 'error', text2: 'Failed to open file' });
                return;
            }
            const a = document.createElement('a');
            a.href = signedUrl;
            a.download = fileObj.filename || fileObj.name || 'file';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            loader.disable();
        } else {
            const file = await getFile(fileObj.key);
            if (file) {
                FileViewer.open(file, { showOpenWithDialog: true, showAppsSuggestions: true }).then(() => loader.disable());
            } else {
                loader.disable();
                Toast.show({ type: 'error', text2: 'Failed to open file' });
            }
        }
    };

    useEffect(() => {
        if (loggedInUser && (!personID || (!profile?.id && !fetchingProfile))) {
            setNotFound("We couldn't find that person");
        } else if (loggedInUser && (!fetchingProfile && (!loggedInUser?.can?.("View People") && loggedInUser?.id != personID))) {
            setNotFound("You don't have permission to view this profile");
        } else {
            setNotFound(null);
        }
    }, [fetchingProfile, personID, profile?.id, loggedInUser]);

    // Apply tab param to set the correct tab on load.
    // Wait until profile is loaded so activityRoutes is complete, and re-resolve
    // the index at fire time against the latest routes to avoid stale-index races
    // when family/loggedInUser load after profile and shift tab positions.
    const activityRoutesRef = useRef(activityRoutes);
    activityRoutesRef.current = activityRoutes;

    useEffect(() => {
        if (!tab || initialPageApplied || fetchingProfile) return;
        if (activityRoutes.length === 0) return;

        const timeout = setTimeout(() => {
            const routes = activityRoutesRef.current;
            const activityIndex = routes.findIndex(r => r.key === tab || r.title === tab);
            if (activityIndex !== -1) {
                setActivityTab(activityIndex);
                // For mobile, add 1 to account for the Profile tab at index 0
                setMobileTab(activityIndex + 1);
            }
            setInitialPageApplied(true);
        }, 100);

        return () => clearTimeout(timeout);
    }, [tab, initialPageApplied, fetchingProfile, activityRoutes]);

    // Update tab param when tab changes (for refresh persistence) — skip in modal context
    const handleActivityTabChange = useCallback((index) => {
        setActivityTab(index);
        if(forceSingleColumn) return;
        const tabKey = activityRoutes[index]?.key;
        if (tabKey) {
            InteractionManager.runAfterInteractions(() => router.setParams({ tab: tabKey }));
        }
    }, [activityRoutes, router, forceSingleColumn]);

    const handleMobileTabChange = useCallback((index) => {
        setMobileTab(index);
        if(forceSingleColumn) return;
        // For mobile, index 0 is Profile, so subtract 1 for activity routes
        const tabKey = index === 0 ? 'profile' : activityRoutes[index - 1]?.key;
        if (tabKey && tabKey !== 'profile') {
            InteractionManager.runAfterInteractions(() => router.setParams({ tab: tabKey }));
        } else if (tabKey === 'profile') {
            InteractionManager.runAfterInteractions(() => router.setParams({ tab: undefined }));
        }
    }, [activityRoutes, router, forceSingleColumn]);

    useEffect(() => {
        const options = [];
        if (loggedInUser?.can?.("Edit People") || (loggedInUser?.id == profile?.id)) {
            options.push({
                name: loggedInUser?.id == profile?.id ? "Edit My Profile" : "Edit Profile",
                icon: dropdownIcons.edit,
                onSelect: () => router.push({ pathname: "/people/create", params: { id: profile?.id } })
            });
        }
        if (loggedInUserIsAdmin && currentProfileIsRegistered) {
            options.push({
                name: currentProfileIsAdmin ? "Remove Admin" : "Make Admin",
                icon: { android: "settings", ios: "shield.lefthalf.fill", web: "shield" },
                onSelect: () => {
                    showAlert({
                        title: currentProfileIsAdmin ? `Remove ${profile?.firstName} as admin?` : `Make ${profile?.firstName} an admin?`,
                        subtitle: currentProfileIsAdmin ? "Remove admin privileges?" : "This gives full organization access.",
                        onConfirm: () => adminStatusMutation.mutate({ personID: profile?.id, isAdmin: !currentProfileIsAdmin })
                    });
                }
            });
        }
        if (loggedInUserIsAdmin) {
            options.push({
                name: "Emulate Permissions",
                icon: { android: "people", ios: "person.2.fill", web: "people" },
                onSelect: () => {
                    loader.enable();
                    getPersonProfile(profile?.id).then(p => {
                        if(!p){
                            Toast.show({
                                type: 'error',
                                text1: 'Error',
                                text2: 'Failed to emulate user'
                            });
                            loader.disable();
                            return;
                        }

                        queryClient.setQueryData(QueryKeys.people.loggedInUser(), p);
                        setEmulation(true, `${p?.firstName} ${p?.lastName}`);
                        loader.disable();
                    });
                }
            });
        }
        if (loggedInUser?.can?.("Archive People") && !currentProfileIsRegistered) {
            const isArchived = profile?.isArchived;
            options.push({
                name: isArchived ? "Unarchive Person" : "Archive Person",
                icon: { android: "archive", ios: "archivebox.fill", web: "archive" },
                destructive: !isArchived,
                onSelect: () => {
                    showAlert({
                        title: isArchived ? `Unarchive ${profile?.firstName}?` : `Archive ${profile?.firstName}?`,
                        subtitle: isArchived ? "They will appear in directory and check-in again." : "They won't appear in directory or check-in.",
                        onConfirm: () => archivePersonMutation.mutate(profile?.id),
                        isDestructive: !isArchived,
                        confirmText: isArchived ? "Unarchive" : "Archive"
                    });
                }
            });
        }
        if (loggedInUser?.can?.("Invite Users") && !currentProfileIsRegistered && profile?.email) {
            options.push({
                name: "Invite to Reach",
                icon: { android: "mail", ios: "envelope.fill", web: "mail" },
                onSelect: async () => {
                    showAlert({
                        title: `Invite ${profile?.firstName} to Reach?`,
                        subtitle: `An invitation email will be sent to ${profile?.email}`,
                        confirmText: "Send Invite",
                        onConfirm: async () => {
                            try {
                                loader.enable();
                                const orgID = await getCurrentOrgID();
                                const org = await getOrganization(orgID);
                                await sendInvitation({
                                    personID: profile?.id,
                                    email: __DEV__ ? "jeremy@invendevokc.com" : profile?.email,
                                    firstName: profile?.firstName,
                                    organizationName: org?.name,
                                    inviterName: `${loggedInUser?.firstName} ${loggedInUser?.lastName}`
                                });
                                loader.disable();
                            } catch {
                                loader.disable();
                            }
                        }
                    });
                }
            });
        }
        options.push({
            name: "Export as PDF",
            icon: { android: "description", ios: "doc.fill", web: "download-outline" },
            onSelect: () => setShowPDFExport(true)
        });
        if(!forceSingleColumn){
            navigation.setOptions({
                headerTitle: () => null,
                title: profile?.firstName ? `${profile?.firstName} ${profile?.lastName}` : "Person",
                headerRight: options.length == 0 || notFound ? null : () => <DropdownMenu options={options} />,
            });
        }
    }, [navigation, profile, notFound, loggedInUser, loggedInUserIsAdmin, currentProfileIsAdmin, currentProfileIsRegistered, forceSingleColumn]);

    // ============ INFO ROW COMPONENT ============
    const InfoRow = useCallback(({ icon, label, value, onPress, children, copyValue, fieldType, onEdit }) => {
        if (!value && !children) return null;

        const buildContextMenuItems = () => {
            const items = [];
            const textToCopy = copyValue || value;
            
            if (textToCopy) {
                items.push({ key: 'copy', label: 'Copy', icon: contextMenuIcons.copy, onSelect: () => Clipboard.setString(textToCopy) });
            }
            if (fieldType === 'email' && value) {
                items.push({ key: 'email', label: 'Send Email', icon: contextMenuIcons.email, onSelect: () => Linking.openURL(`mailto:${value}`).catch(() => Toast.show({ type: 'error', text2: 'No email app found.' })) });
            }
            if (fieldType === 'phone' && value) {
                items.push({ key: 'phone', label: 'Call', icon: contextMenuIcons.phone, onSelect: () => openURL(`tel:${value}`) });
                items.push({ key: 'sms', label: 'Text', icon: contextMenuIcons.sms, onSelect: () => openURL(`sms:${value}`) });
            }
            if (fieldType === 'address' && value) {
                items.push({ key: 'maps', label: 'Open in Maps', icon: contextMenuIcons.map, onSelect: () => {
                    const destination = encodeURIComponent(value);
                    const provider = Platform.OS === 'ios' ? 'apple' : 'google';
                    openURL(`http://maps.${provider}.com/?daddr=${destination}`);
                }});
                if (loggedInUser?.can?.("Edit People") || (loggedInUser?.id == profile?.id)) {
                    items.push({ key: 'editAddress', label: 'Edit Address', icon: contextMenuIcons.edit, onSelect: () => {
                        router.push({ pathname: "/addresses/create", params: { id: profile?.addressID, fromScreen: `/people/${personID}`, fromStoreKey: personID, fromPersonID: personID } });
                    }});
                }
            }
            return items;
        };

        const content = (
            <View style={styles.infoRow}>
                <View style={styles.infoRowLeft}>
                    <Unicon name={icon} size={18} colorVariant="textSecondary" />
                    <Text style={styles.infoRowLabel}>{label}</Text>
                </View>
                <View style={styles.infoRowRight}>
                    {children || (
                        <Text selectable={onPress ? false : true} style={[styles.infoRowValue, onPress && styles.linkText]}>
                            {value}
                        </Text>
                    )}
                    {(isWeb && onEdit) && (
                        <TouchableOpacity onPress={onEdit} style={styles.infoRowEditBtn}>
                            <Unicon name="pencil" size={14} colorVariant="textSecondary" />
                        </TouchableOpacity>
                    )}
                </View>
            </View>
        );

        if (onPress) {
            return (
                <ContextMenu items={buildContextMenuItems()}>
                    <TouchableOpacity onPress={onPress}>
                        {content}
                    </TouchableOpacity>
                </ContextMenu>
            );
        }

        return content;
    }, [loggedInUser, profile, personID, router]);

    // ============ PROFILE INFO PANEL ============
    const profileInfoPanel = useMemo(() => {
        const dob = profile?.dateOfBirth ? DateTime.fromISO(profile.dateOfBirth) : null;
        const smsPhone = profile?.Phones?.find(p => p.smsOptInStatus === 'opted_in') || profile?.Phones?.[0];

        const customFieldSections = personCustomFieldSections?.map(s => {
            const fields = s.CustomFields?.filter(item => customFieldValues?.[item.id]);
            if (!fields?.length) return null;
            return { name: s.name, fields };
        }).filter(Boolean) || [];

        return (
            <View style={styles.leftPanel}>
                <ScrollView 
                    contentContainerStyle={styles.leftPanelContent}
                    keyboardShouldPersistTaps="handled"
                    refreshControl={<RefreshControl refreshing={fetchingProfile} onRefresh={refresh}/>}
                    showsVerticalScrollIndicator={false}
                >
                    {/* Header Card */}
                    <Animated.View entering={FadeInDown.duration(400)} style={styles.headerCard}>
                        <View style={styles.avatarRow}>
                            <View style={styles.avatarWrapper}>
                                <Avatar size={80} profile={profile} editable={loggedInUser?.id == profile?.id || loggedInUser?.can?.("Edit Profile Picture")} disableBorder />
                            </View>
                            <View style={styles.headerInfo}>
                                <View style={styles.nameRow}>
                                    <Text style={styles.profileName}>{profile?.firstName} {profile?.lastName}</Text>
                                    {currentProfileIsRegistered && (
                                        <Tooltip title="Registered user">
                                            <MaterialIcon name="verified" size={18} color={styles.verifiedColor.color} />
                                        </Tooltip>
                                    )}
                                    {currentProfileIsAdmin && (
                                        <Tooltip title="Admin">
                                            <MaterialIcon name="admin-panel-settings" size={18} color={styles.adminColor.color} />
                                        </Tooltip>
                                    )}
                                </View>
                                {/* {profile?.Roles?.filter(r => !r.isHidden)?.[0] && (
                                    <Text style={styles.profileSubtitle}>{profile.Roles.filter(r => !r.isHidden)[0].name}</Text>
                                )} */}
                                {profile?.isArchived && (
                                    <View style={styles.archivedBadge}>
                                        <Unicon name="archive" size={12} colorVariant="error" />
                                        <Text style={styles.archivedText}>Archived</Text>
                                    </View>
                                )}
                            </View>
                        </View>

                        {/* Stats Row */}
                        <View style={styles.statsRow}>
                            <View style={styles.statItem}>
                                <Text style={styles.statLabel}>Last Attended</Text>
                                <Text style={styles.statValue}>{profile?.mostRecentVisit ? parseTimeFrame(profile.mostRecentVisit) : '-'}</Text>
                            </View>
                            <View style={styles.statDivider} />
                            <View style={styles.statItem}>
                                <Text style={styles.statLabel}>Total Visits</Text>
                                <Text style={styles.statValue}>{profile?.timesAttended || 0}</Text>
                            </View>
                            <View style={styles.statDivider} />
                            <View style={styles.statItem}>
                                <Text style={styles.statLabel}>First Visit</Text>
                                <Text style={styles.statValue}>{profile?.firstVisit ? parseTimeFrame(profile.firstVisit) : '-'}</Text>
                            </View>
                        </View>

                        {/* Family Avatars */}
                        {(canView('View Basic Info') && family.length > 0) && (
                            <View style={styles.familyRow}>
                                {family.slice(0, 5).map((fm) => (
                                    <Avatar key={fm.id} size={35} profile={fm} disableBorder onPress={() => router.push(`/people/${fm.id}`)}/>
                                ))}
                                {family.length > 5 && (
                                    <View style={styles.moreBadge}>
                                        <Text style={styles.moreBadgeText}>+{family.length - 5}</Text>
                                    </View>
                                )}
                            </View>
                        )}

                        <View style={{flexDirection: 'row', gap: 8}}>
                            {(canView('View Contact Info') && profile?.Phones?.[0]?.phoneNumber) && (
                                <ActionButton 
                                    iconName="call" 
                                    text="Call" 
                                    onPress={() => handlePhonePress(profile.Phones[0].phoneNumber)} 
                                />
                            )}

                            {(!isWeb && canView('View Contact Info') && smsPhone?.phoneNumber) && (
                                <ActionButton 
                                    iconName="chatbubble" 
                                    text="Text" 
                                    onPress={() => openURL(`sms:${smsPhone.phoneNumber.replace(/ /g, '')}`)} 
                                />
                            )}

                            {(canView('View Contact Info') && !!profile?.email) && (
                                <ActionButton 
                                    iconName="mail" 
                                    text="Email" 
                                    onPress={() => Linking.openURL(`mailto:${profile.email}`).catch(() => Toast.show({ type: 'error', text2: 'No email app found.' }))} 
                                />
                            )}
                        </View>
                    </Animated.View>

                {/* Info Rows */}
                <Animated.View entering={FadeInDown.delay(150).duration(400)} style={styles.infoSection}>
                    {(canView('View Basic Info') && !!profile?.gender) && (
                        <InfoRow icon="person" label="Gender" value={profile?.gender} />
                    )}
                    
                    {(canView('View Basic Info') && profile?.dateOfBirth && dob) && (
                        <InfoRow icon="calendar" label="Birthday">
                            <View>
                                <Text selectable style={styles.infoRowValue}>{dob.toLocaleString({ day: "numeric", month: "long", year: "numeric" })}</Text>
                                <Text selectable style={styles.infoRowValueSub}>{getAgeString(profile.dateOfBirth)} old</Text>
                            </View>
                        </InfoRow>
                    )}

                    {(canView('View Contact Info') && !!profile?.Address) && (
                        <InfoRow
                            icon="location"
                            label="Mailing Address"
                            value={`${profile.Address.street}${profile.Address.streetLine2 ? `, ${profile.Address.streetLine2}` : ''}, ${profile.Address.city}, ${profile.Address.state} ${profile.Address.zip}`}
                            copyValue={`${profile.Address.street}${profile.Address.streetLine2 ? `, ${profile.Address.streetLine2}` : ''}, ${profile.Address.city}, ${profile.Address.state} ${profile.Address.zip}`}
                            fieldType="address"
                            onPress={() => router.push('/addresses/' + profile.addressID)}
                            onEdit={(loggedInUser?.can?.("Edit People") || (loggedInUser?.id == profile?.id)) ? () => router.push({ pathname: "/addresses/create", params: { id: profile?.addressID, fromScreen: `/people/${personID}`, fromStoreKey: personID, fromPersonID: personID } }) : undefined}
                        >
                            <View>
                                <Text style={styles.linkText}>{profile.Address.street}</Text>
                                {profile.Address.streetLine2 && <Text style={styles.linkText}>{profile.Address.streetLine2}</Text>}
                                <Text style={styles.infoRowValueSub}>{profile.Address.city}, {profile.Address.state} {profile.Address.zip}</Text>
                            </View>
                        </InfoRow>
                    )}

                    {(canView('View Contact Info') && !!profile?.email) && (
                        <InfoRow
                            icon="mail"
                            label="Email"
                            value={profile.email}
                            onPress={() => Linking.openURL(`mailto:${profile.email}`).catch(() => Toast.show({ type: 'error', text2: 'No email app found.' }))}
                            copyValue={profile.email}
                            fieldType="email"
                        >
                            <Text style={styles.linkText}>{profile.email}</Text>
                        </InfoRow>
                    )}

                    {(canView('View Contact Info') && profile?.Phones?.length > 0) && (
                        profile.Phones.map((p, i) => (
                            <InfoRow
                                key={i}
                                value={p.phoneNumber}
                                onPress={() => openURL(`tel:${p.phoneNumber.replace(/ /g, '')}`)}
                                fieldType="phone"
                                icon="call"
                                label={p.label || `Phone ${i + 1}`}
                            >
                                <View style={{ alignItems: 'flex-end', gap: 4 }}>
                                    <Text style={styles.linkText}>{p.phoneNumber}</Text>
                                    <View style={{ flexDirection: 'row', gap: 6 }}>
                                        {p.smsOptInStatus === 'opted_in' && (
                                            <Tooltip title="This person is opted into SMS communications.">
                                                <View style={styles.phoneBadgeSms('success')}>
                                                    <Unicon name="chatbubble-ellipses" size={10} colorVariant="onSuccess" />
                                                    <Text style={styles.phoneBadgeText('onSuccess')}>SMS Enabled</Text>
                                                </View>
                                            </Tooltip>
                                        )}
                                        {p.smsOptInStatus === 'opt_in_paused' && (
                                            <Tooltip title="This person has opted into SMS, but we've disabled SMS communication on their profile. To continue sending them SMS communications, re-enable it in the profile editor.">
                                                <View style={styles.phoneBadgeSms('warning')}>
                                                    <Unicon name="pause" size={10} colorVariant="onWarning" />
                                                    <Text style={styles.phoneBadgeText('onWarning')}>Paused</Text>
                                                </View>
                                            </Tooltip>
                                        )}
                                        {p.smsOptInStatus === 'opted_out' && (
                                            <Tooltip title="This person has blocked SMS communications by replying STOP to a text message. They cannot be sent SMS communications.">
                                                <View style={styles.phoneBadgeSms('error')}>
                                                    <Unicon name="ban" size={10} colorVariant="onError" />
                                                    <Text style={styles.phoneBadgeText('onError')}>Opted Out</Text>
                                                </View>
                                            </Tooltip>
                                        )}
                                        {p.smsOptInStatus === 'non_mobile' && (
                                            <Tooltip title="This phone number is not a mobile phone, so SMS messages cannot be sent to it. SMS has been disabled for this number.">
                                                <View style={styles.phoneBadgeSms('textSecondaryContainer')}>
                                                    <Unicon name="phone-off" library="material" size={10} colorVariant="textSecondary" />
                                                    <Text style={styles.phoneBadgeText('textSecondary')}>Not a Mobile Phone</Text>
                                                </View>
                                            </Tooltip>
                                        )}
                                        {p.private && (
                                            <View style={styles.phoneBadgePrivate}>
                                                <Unicon name="lock-closed" size={10} colorVariant="textSecondary" />
                                                <Text style={[styles.phoneBadgeText, styles.phoneBadgeTextPrivate]}>Private</Text>
                                            </View>
                                        )}
                                    </View>
                                </View>
                            </InfoRow>
                        ))
                    )}

                    {(canView('View Basic Info') && (!!profile?.InvitedBy || !!profile?.invitedByNote)) && (
                        <InfoRow icon="people" label="Invited By" onPress={profile?.InvitedBy ? () => router.push(`/people/${profile.InvitedBy.id}`) : undefined}>
                            <View>
                                {profile?.InvitedBy && (
                                    <View style={styles.invitedByRow}>
                                        <Avatar size={24} profile={profile.InvitedBy} />
                                        <Text style={styles.linkText}>{profile.InvitedBy.firstName} {profile.InvitedBy.lastName}</Text>
                                    </View>
                                )}
                                {profile?.invitedByNote && (
                                    <Text style={styles.invitedByNoteText}>{profile.invitedByNote}</Text>
                                )}
                            </View>
                        </InfoRow>
                    )}

                    {(!isFree && canView('View Custom Fields') && customFieldSections.map(section => (
                        <View key={section.name}>
                            <Text style={styles.sectionLabel}>{section.name}</Text>
                            {section.fields.map(field => (
                                <InfoRow 
                                    key={field.id} 
                                    icon="document-text" 
                                    label={field.name}
                                    value={formatCustomFieldValue(customFieldValues[field.id], field.type)}
                                    onPress={field.type === "File Upload" ? () => handleFileOpen(customFieldValues[field.id]) : null}
                                />
                            ))}
                        </View>
                    )))}

                    {(canView('View Basic Info') && (profile?.createdAt || profile?.updatedAt)) && (
                        <View style={styles.metaSection}>
                            {profile?.createdAt && (
                                <View style={styles.metaRow}>
                                    <Text style={styles.metaLabel}>Added</Text>
                                    <Text testID="person-added-date" style={styles.metaValue}>
                                        {DateTime.fromISO(profile.createdAt).toLocaleString(DateTime.DATE_MED)}
                                    </Text>
                                </View>
                            )}
                            {profile?.updatedAt && (
                                <View style={styles.metaRow}>
                                    <Text style={styles.metaLabel}>Last Updated</Text>
                                    <Text testID="person-updated-date" style={styles.metaValue}>
                                        {DateTime.fromISO(profile.updatedAt).toLocaleString(DateTime.DATE_MED)}
                                    </Text>
                                </View>
                            )}
                        </View>
                    )}
                    </Animated.View>
                </ScrollView>
            </View>
        );
    }, [profile, fetchingProfile, loggedInUser, currentProfileIsRegistered, currentProfileIsAdmin, family, customFieldValues, personCustomFieldSections, isWeb, isFree]);

    // ============ ACTIVITY PANEL ============
    const renderActivityTabBar = useCallback((props) => <ReachTabBar {...props} scrollEnabled={activityRoutes.length > 4} />, [activityRoutes.length]);

    const renderActivityScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'tasks':
                return <PersonTasksTab tasks={profile?.TaskInstances} personID={personID} fromScreen={fromScreen} />;
            case 'attendance':
                return <AttendanceTab personID={personID} />;
            case 'groups':
                return <SmallGroupsTab profile={profile} />;
            case 'roles':
                return (
                    <View style={styles.tabContent}>
                        {loggedInUserIsAdmin && (
                            <View style={styles.toggleRow}>
                                <SegmentedTabs
                                    tabs={[{ key: 'roles', label: 'Roles' }, { key: 'permissions', label: 'Permissions' }]}
                                    value={rolesViewType}
                                    onChange={setRolesViewType}
                                    testIDPrefix="person-roles-view"
                                />
                            </View>
                        )}
                        {loggedInUserIsAdmin && rolesViewType === "permissions" ? (
                            <View style={styles.tabContent}>
                                {/* Search Input */}
                                <View style={{ paddingBottom: 8, paddingHorizontal: 5 }}>
                                    <SearchBox
                                        searchText={permissionsSearchQuery}
                                        placeholder="Search permissions..."
                                        onChangeText={setPermissionsSearchQuery}
                                        textChangeDelay={0}
                                    />
                                </View>

                                <ScrollView style={styles.tabContent} keyboardShouldPersistTaps="handled">
                                    {filteredPermissionsByModule.length === 0 && permissionsSearchQuery ? (
                                        <View style={styles.emptySearchContainer}>
                                            <Text style={styles.emptySearchText}>No permissions match &ldquo;{permissionsSearchQuery}&rdquo;</Text>
                                        </View>
                                    ) : (
                                        filteredPermissionsByModule.map(([moduleName, modulePermissions]) => (
                                            <View key={moduleName} style={styles.permissionsModuleSection}>
                                                <Text style={styles.permissionsModuleHeader}>{moduleName}</Text>
                                                {modulePermissions.map(perm => (
                                                    <Pressable
                                                        key={perm.id}
                                                        onPress={() => router.push({ pathname: `/permissions/${perm.id}` })}
                                                        style={styles.roleItem}
                                                    >
                                                        <View>
                                                            <Unicon name="key" size={24} colorVariant="primary" />
                                                        </View>
                                                        <View style={styles.roleItemContent}>
                                                            <Text style={styles.roleItemTitle}>{perm.name}</Text>
                                                            {perm.description && <Text style={styles.roleItemDesc}>{perm.description}</Text>}
                                                            {perm.grantedByRoles?.length > 0 && (
                                                                <View style={styles.grantedBySection}>
                                                                    <Text style={styles.grantedByLabel}>Granted through:</Text>
                                                                    <View style={styles.grantedByRow}>
                                                                        {perm.grantedByRoles.map(({roleName, roleID}) => (
                                                                            <Pressable onPress={() => router.push(`/roles/${roleID}`)} key={roleID} style={styles.grantedByPill}>
                                                                                <Unicon name="shield-checkmark" size={12} colorVariant="primary" />
                                                                                <Text style={styles.grantedByPillText}>{roleName}</Text>
                                                                            </Pressable>
                                                                        ))}
                                                                    </View>
                                                                </View>
                                                            )}
                                                        </View>
                                                    </Pressable>
                                                ))}
                                            </View>
                                        ))
                                    )}
                                    {filteredPermissionsByModule.length === 0 && !permissionsSearchQuery && (
                                        <EmptyListPlaceholder message="No permissions" />
                                    )}
                                </ScrollView>
                            </View>
                        ) : (
                            <LegendList
                                recycleItems
                                keyExtractor={(item) => item.id}
                                estimatedItemSize={60}
                                data={profile?.Roles?.filter(r => isRoleVisibleToUser(r, loggedInUser))}
                                renderItem={({ item }) => (
                                    <Pressable onPress={() => router.push({ pathname: `/roles/${item.id}` })} style={styles.roleItem}>
                                        <View>
                                            <Unicon name="people" size={24} colorVariant="primary" />
                                        </View>
                                        <View style={styles.roleItemContent}>
                                            <Text style={styles.roleItemTitle}>{item.name}</Text>
                                            {item.description && <Text style={styles.roleItemDesc}>{item.description}</Text>}
                                        </View>
                                    </Pressable>
                                )}
                                ListEmptyComponent={<EmptyListPlaceholder message="No roles" />}
                                contentContainerStyle={styles.listPadding}
                                keyboardShouldPersistTaps="handled"
                            />
                        )}
                    </View>
                );
            case 'family':
                return (
                    <ScrollView style={styles.tabContent} contentContainerStyle={styles.listPadding} keyboardShouldPersistTaps="handled">
                        {family.map((fm) => (
                            <ProfileCard key={fm.id} profile={fm} subtitle={fm.familyRole} sameHousehold={fm?.addressID && profile?.addressID && fm?.addressID === profile?.addressID} />
                        ))}
                    </ScrollView>
                );
            case 'notes':
                return (
                    <NotesTab
                        personID={personID}
                        loggedInUser={loggedInUser}
                        loggedInUserIsAdmin={loggedInUserIsAdmin}
                    />
                );
            case 'followup':
                return (
                    <FollowUpTab personID={personID} />
                );
            case 'files':
                return (
                    <View style={styles.tabContent}>
                        <FilesScreen personID={personID} embedded />
                    </View>
                );
            case 'training':
                return (
                    <View style={styles.tabContent}>
                        <TrainingProgressTab personID={personID} />
                    </View>
                );
            case 'giving':
                return (
                    <GivingTab personID={personID} person={profile} family={family} canViewContributions={canViewContributions} />
                );
            case 'badges':
                return (
                    <PersonMilestonesTab personID={personID} isOwnProfile={isOwnProfile} />
                );
            case 'blackouts':
                return (
                    <BlackoutCalendarTab
                        profile={profile}
                        loggedInUser={loggedInUser}
                        router={router}
                    />
                );
            default:
                return null;
        }
    }, [profile, personID, fromScreen, loggedInUser, loggedInUserIsAdmin, family, effectivePermissions, rolesViewType, permissionsSearchQuery]);


    // ============ MOBILE LAYOUT ============
    const mobileRoutes = useMemo(() => [{ key: 'profile', title: 'Profile' }, ...activityRoutes], [activityRoutes]);
    const renderMobileTabBar = useCallback((props) => <ReachTabBar {...props} scrollEnabled />, []);

    const renderMobileScene = useCallback(({ route }) => {
        if (route.key === 'profile') return profileInfoPanel;
        // For non-profile tabs, render the activity content directly (no nested TabView)
        return renderActivityScene({ route });
    }, [profileInfoPanel, renderActivityScene]);

    // ============ MAIN RENDER ============
    const renderLoadingState = () => (
        <Animated.View entering={FadeIn.duration(300)} style={styles.loadingContainer}>
            {/* Avatar + Name */}
            <View style={styles.loadingHeaderRow}>
                <ContentLoader containerHeight={80} containerWidth={80} borderRadius={40} spacingVertical={0} />
                <View style={styles.loadingHeaderInfo}>
                    <ContentLoader containerHeight={22} containerWidth="70%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                    <ContentLoader containerHeight={14} containerWidth="40%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                </View>
            </View>
            {/* Stats Row */}
            <View style={styles.loadingStatsRow}>
                <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
            </View>
            {/* Action Buttons */}
            <View style={styles.loadingActionsRow}>
                <ContentLoader containerHeight={44} borderRadius={10} staggerPosition={3} spacingVertical={0} style={styles.loadingActionItem} />
                <ContentLoader containerHeight={44} borderRadius={10} staggerPosition={3} spacingVertical={0} style={styles.loadingActionItem} />
                <ContentLoader containerHeight={44} borderRadius={10} staggerPosition={3} spacingVertical={0} style={styles.loadingActionItem} />
            </View>
            {/* Info Rows */}
            <ContentLoader containerHeight={180} borderRadius={12} staggerPosition={4} />
        </Animated.View>
    );

    if (!profile?.id) return renderLoadingState();
    if (notFound) return <EmptyListPlaceholder type="404" message={notFound} />;

    if (isWideScreen) {
        return (
            <View style={styles.container}>
                {showPDFExport && (
                    <PersonProfilePDF
                        profile={profile}
                        customFieldValues={customFieldValues}
                        personCustomFieldSections={personCustomFieldSections}
                        trainingProgress={trainingProgress}
                        onClose={() => setShowPDFExport(false)}
                    />
                )}
                <View style={styles.twoColumn}>
                    <View style={styles.leftPanelWrapper}>
                        {profileInfoPanel}
                    </View>
                    {activityRoutes.length > 0 && <TabView
                        navigationState={{ index: Math.min(activityTab, Math.max(0, activityRoutes.length - 1)), routes: activityRoutes }}
                        renderScene={renderActivityScene}
                        onIndexChange={handleActivityTabChange}
                        renderTabBar={renderActivityTabBar}
                        swipeEnabled={isNative}
                        lazy
                    />}
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {showPDFExport && (
                <PersonProfilePDF
                    profile={profile}
                    customFieldValues={customFieldValues}
                    personCustomFieldSections={personCustomFieldSections}
                    trainingProgress={trainingProgress}
                    onClose={() => setShowPDFExport(false)}
                />
            )}
            <TabView
                navigationState={{ index: Math.min(mobileTab, mobileRoutes.length - 1), routes: mobileRoutes }}
                renderScene={renderMobileScene}
                onIndexChange={handleMobileTabChange}
                renderTabBar={renderMobileTabBar}
                swipeEnabled
                lazy
            />
        </View>
    );
};

const styles = StyleSheet.create((theme, rt) => ({
    container: { flex:1, backgroundColor: 'transparent' },
    loadingContainer: { flex: 1, padding: 20, maxWidth: 500, width: rt.isLandscape ? '50%' : '100%' },
    loadingHeaderRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 20 },
    loadingHeaderInfo: { flex: 1, marginLeft: 16, justifyContent: 'center' },
    loadingStatsRow: { flexDirection: 'row', gap: 10, marginBottom: 20 },
    loadingStatItem: { flex: 1 },
    loadingActionsRow: { flexDirection: 'row', gap: 10, marginBottom: 20 },
    loadingActionItem: { flex: 1 },
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanelWrapper: { flex: 1, maxWidth: 420 },
    leftPanel: { flex: 1 },
    leftPanelContent: { paddingTop: 20, paddingHorizontal: rt.isLandscape ? 20 : 10, paddingBottom: 100 },
    rightPanel: { flex: 2, flexGrow: 1 },

    // Header Card
    headerCard: { backgroundColor: theme.colors.surface, borderRadius: 16, padding: 20, marginBottom: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 8, elevation: 3 },
    avatarRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16 },
    avatarWrapper: { position: 'relative' },
    visitsBadge: { position: 'absolute', top: -4, right: -4, backgroundColor: theme.colors.primary, borderRadius: 12, minWidth: 24, height: 24, alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: theme.colors.surface },
    visitsBadgeText: { color: theme.colors.surface, fontSize: 11, fontFamily: theme.fonts.bold },
    headerInfo: { flex: 1, marginLeft: 16 },
    nameRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
    profileName: { fontSize: 20, fontFamily: theme.fonts.heading, color: theme.colors.text },
    profileSubtitle: { fontSize: 14, color: theme.colors.textSecondary, marginTop: 2 },
    verifiedColor: { color: theme.colors.info },
    adminColor: { color: theme.colors.warning },
    archivedBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: theme.colors.errorContainer, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 4, marginTop: 6, alignSelf: 'flex-start', gap: 4 },
    archivedText: { color: theme.colors.error, fontSize: 11, fontFamily: theme.fonts.bold },

    // Stats Row
    statsRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, borderTopWidth: 1, borderBottomWidth: 1, borderColor: theme.colors.border, marginBottom: 14 },
    statItem: { flex: 1, alignItems: 'center' },
    statDivider: { width: 1, height: 28, backgroundColor: theme.colors.border },
    statLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 2 },
    statValue: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Family Row
    familyRow: { flexDirection: 'row', gap: 6, marginBottom: 14 },
    moreBadge: { width: 32, height: 32, borderRadius: 16, backgroundColor: theme.colors.border, alignItems: 'center', justifyContent: 'center' },
    moreBadgeText: { fontSize: 11, color: theme.colors.textSecondary, fontFamily: theme.fonts.bold },

    // Action Buttons
    actionRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
    actionBtnPrimary: { flex: 1, minWidth: 100, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, backgroundColor: theme.colors.primary },
    actionBtnPrimaryText: { color: theme.colors.surface, fontSize: 13, fontFamily: theme.fonts.bold },
    actionBtn: { flex: 1, minWidth: 100, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
    actionBtnText: { color: theme.colors.text, fontSize: 13, fontFamily: theme.fonts.bold },

    // Info Section
    infoSection: { backgroundColor: theme.colors.surface, borderRadius: 16, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 2 },
    infoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: theme.colors.border },
    infoRowLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, maxWidth: 140 },
    infoRowLabel: { fontSize: 13, color: theme.colors.textSecondary },
    infoRowRight: { flex: 2, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8 },
    infoRowEditBtn: { padding: 4, opacity: 0.6 },
    infoRowValue: { fontSize: 14, color: theme.colors.text, textAlign: 'right' },
    infoRowValueSub: { fontSize: 12, color: theme.colors.textSecondary, textAlign: 'right', marginTop: 2 },
    linkText: { fontSize: 14, color: theme.colors.primary, textAlign: 'right' },
    invitedByRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    invitedByNoteText: { fontSize: 12, color: theme.colors.textSecondary, fontStyle: 'italic', marginTop: 2, textAlign: "right" },
    phoneBadgeSms: (variant = 'primary') => ({ flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: theme.colors[variant], borderRadius: 10, paddingHorizontal: 6, paddingVertical: 2 }),
    phoneBadgePrivate: { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: theme.colors.surface, borderRadius: 10, paddingHorizontal: 6, paddingVertical: 2, borderWidth: 1, borderColor: theme.colors.border },
    phoneBadgeText: (variant = 'onPrimary') => ({ fontSize: 9, fontFamily: theme.fonts.bold, color: theme.colors[variant] }),
    phoneBadgeTextPrivate: { color: theme.colors.textSecondary },
    sectionLabel: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase', marginTop: 16, marginBottom: 4 },

    // Meta (created / updated)
    metaSection: { marginTop: 12, paddingTop: 12, flexDirection:'row', justifyContent:"space-between", gap: 8 },
    metaRow: { gap: 2 },
    metaLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 },
    metaValue: { fontSize: 12, color: theme.colors.text },

    // Tags
    tagsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' },
    tag: { backgroundColor: theme.colors.primaryContainer, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4 },
    tagText: { fontSize: 12, color: theme.colors.primary, fontFamily: theme.fonts.bold },

    // Activity Panel
    tabContent: { flex: 1},
    listPadding: { padding: 16, paddingBottom: 100 },
    toggleRow: { paddingHorizontal: 16, paddingTop: 8, paddingBottom: 12 },
    groupsFilters: { paddingHorizontal: 16, paddingTop: 8, paddingBottom: 4 },
    groupsSelectRow: { flexDirection: 'row', gap: 10 },
    groupsSelect: { flex: 1 },
    addBtnRow: { padding: 16, alignItems: 'center' },
    blackoutHeaderSection: { paddingVertical: 16, paddingHorizontal: 4, marginBottom: 8 },
    blackoutHeaderTitle: { fontSize: 24, fontFamily: theme.fonts.bold, color: theme.colors.text, marginBottom: 6 },
    blackoutHeaderDescription: { fontSize: 14, fontFamily: theme.fonts.medium, color: theme.colors.textSecondary, lineHeight: 20 },
    roleItem: { backgroundColor: theme.colors.surface, borderRadius: 10, padding: 14, marginBottom: 8, boxShadow: theme.shadows.card, flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
    roleItemContent: { flex: 1 },
    roleItemTitle: { fontSize: 14, fontFamily: theme.fonts.bold, color: theme.colors.text },
    roleItemDesc: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 4 },
    grantedBySection: { marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: theme.colors.border },
    grantedByLabel: { fontSize: 11, color: theme.colors.textSecondary, marginBottom: 6 },
    grantedByRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    grantedByPill: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: 8, backgroundColor: theme.colors.primaryContainer, borderRadius: 16, _web: { transition: 'all 0.1s ease-in-out', _hover: { transform: 'scale(1.1)' }} },
    grantedByPillText: { fontSize: 11, color: theme.colors.primary, fontFamily: theme.fonts.body },
    permissionsModuleSection: { marginBottom: 20 },
    permissionsModuleHeader: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
    emptySearchContainer: {
        paddingVertical: 20,
        alignItems: 'center',
    },
    emptySearchText: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },

}));

export default PersonProfileNew;
